import {formatMoney} from 'accounting'
import _ from 'lodash'

import {applicantPersonTypes, euCountriesCodes, Locale} from '@/utils/constants'
import {Jurisdiction} from '@/utils/countries/constants'
import {currencies, Currency, getFormattingOptions} from '@/utils/currency'
import {DiscountCouponFragment, EntityDataFragment} from '@/utils/graphqlSdk'
import {VatStatus} from '@/utils/invoices/types'
import {calculateOfferingPrice} from '@/utils/pricing/calculations'
import {discountTypes, endToEndGuaranteePercentage} from '@/utils/pricing/constants'
import {prices} from '@/utils/pricing/prices'
import {
  BasePrice,
  BillingEntity,
  CountryFees,
  DiscountType,
  DynamicPriceSheets,
  Fee,
  FeesGroup,
  PackageDiscounts,
} from '@/utils/pricing/types'

import {BillingEntityType} from '../validations/common/billingEntity'
import {add, div, mul} from './decimals'

export const calculateVatStatus = (
  billingEntity: BillingEntity,
  billedEntity?: {
    type?: string | null
    address?: {country?: string | null} | null
  } | null,
  forceVatIncluded = false,
): VatStatus => {
  const billingAddress = billedEntity?.address

  const vatIncluded =
    forceVatIncluded ||
    (billingEntity.vatPayer &&
      (billingAddress?.country === billingEntity.registrationCountry ||
        (billedEntity?.type === applicantPersonTypes.physical &&
          euCountriesCodes.includes(billingAddress?.country as string))))

  const reverseVatCharge =
    !vatIncluded &&
    billingAddress?.country !== billingEntity.registrationCountry &&
    !!billingAddress?.country &&
    euCountriesCodes.includes(billingAddress?.country)

  return {vatIncluded, reverseVatCharge}
}

export const calculatePackageDiscounts = (countryList: Jurisdiction[]): Partial<PackageDiscounts> =>
  [...countryList]
    .sort(
      (a, b) =>
        prices.registrationMargins[a][currencies.EUR] -
        prices.registrationMargins[b][currencies.EUR],
    )
    .slice(0, -1)
    .reduce((acc, country) => ({...acc, [country]: 15}), {})

const calculatePriceWithQuantity = (price: number, qty: number, vatRate: number): BasePrice => {
  const vatBase = mul(price, qty)
  const vat = mul(vatBase, vatRate)
  return {
    vatBase,
    vat,
    withVat: add(vatBase, vat),
  }
}

export const createFeeItem = (
  descKey: string,
  unitPrice: number,
  qty: number,
  vatRate: number,
  isGovernment: boolean,
  productId: string | null = null,
): Fee => ({
  descKey,
  qty,
  isGovernment,
  productId,
  unitPrice: calculatePriceWithQuantity(unitPrice, 1, div(vatRate, 100)),
  total: calculatePriceWithQuantity(unitPrice, qty, div(vatRate, 100)),
})

export const createDiscountFeeItem = (
  descKey: string,
  unitPrice: number,
  discount: number,
  discountType: DiscountType,
  vatRate: number,
  isGovernment: boolean,
): Fee | null => {
  const discountValue =
    discountType === discountTypes.percentage ? -mul(unitPrice, div(discount, 100)) : -discount
  if (discountValue === 0) {
    return null
  }
  return createFeeItem(descKey, discountValue, 1, vatRate, isGovernment)
}

export const sumPrices = (prices: BasePrice[]): BasePrice =>
  prices.reduce(
    (first, second) => ({
      vatBase: add(first.vatBase, second.vatBase),
      vat: add(first.vat, second.vat),
      withVat: add(first.withVat, second.withVat),
    }),
    {
      vatBase: 0,
      vat: 0,
      withVat: 0,
    },
  )

export const sumFees = (fees: (Fee | FeesGroup | CountryFees)[]): BasePrice =>
  sumPrices(fees.map(({total}) => total))

export const createDiscountCouponItems = (
  legalFeesTotal: number,
  registrationFeesTotal: number,
  coupon: DiscountCouponFragment,
  vatRate: number,
): Fee[] => {
  const lawyerDiscount = createDiscountFeeItem(
    'coupon-discount-lawyer-discount',
    legalFeesTotal,
    coupon.lawyer_fees_discount,
    coupon.discount_type as DiscountType,
    vatRate,
    false,
  )

  const feeDiscount = createDiscountFeeItem(
    'coupon-discount-fees-discount',
    registrationFeesTotal,
    coupon.fees_discount,
    coupon.discount_type as DiscountType,
    0,
    true,
  )

  return [lawyerDiscount, feeDiscount].filter((discount): discount is Fee => discount !== null)
}

export const createE2eProtectionItem = (total: BasePrice, vatRate: number): Fee =>
  createFeeItem(
    'end-to-end-protection-title',
    mul(total.vatBase, div(endToEndGuaranteePercentage, 100)),
    1,
    vatRate,
    false,
  )

export type PricingRequest = {
  currency: string
  numberOfServices: number
  billing_entity?: EntityDataFragment | BillingEntityType | null
  jurisdictions: Jurisdiction[]
  service_type?: string | null
  classes_count?: number
}

export const calculateCountryDiscount = (
  legalFeesPrice: number,
  discount: number,
  vatRate: number,
): FeesGroup => {
  const fees =
    discount > 0
      ? [
          createDiscountFeeItem(
            `package-discount`,
            legalFeesPrice,
            discount,
            discountTypes.percentage,
            vatRate,
            false,
          ),
        ].filter((fee): fee is Fee => fee !== null)
      : []

  return {
    fees,
    total: sumFees(fees),
  }
}

export const formatPrice = (
  price: number,
  currency: Currency,
  language: Locale,
  hasDecimal: boolean = true,
  useCurrencyCode = false,
) => formatMoney(price, getFormattingOptions(currency, language, hasDecimal, useCurrencyCode))

export const formatCategoryFees = (
  fees: Record<number, number>,
  currency: Currency,
  language: Locale,
) => {
  const options = getFormattingOptions(currency, language, false)

  return Object.entries(fees).reduce(
    (acc, [point, price]) => ({
      ...acc,
      [point]: formatMoney(price, options),
    }),
    {},
  )
}

export const getFormattedPriceForExtraItems = (
  country: Jurisdiction,
  currency: Currency,
  language: Locale,
) => {
  const price = prices.extraItems[country]?.[currency]
  const formattingOptions = getFormattingOptions(currency, language, false)
  return formatMoney(price, formattingOptions)
}

export const getMultiCountryDiscount = (countryFees: CountryFees[]) =>
  countryFees.reduce((a, b) => add(a, b.discounts.total.vatBase), 0)

export const hasPriceWarningTooltip = (country: Jurisdiction) =>
  Object.keys(prices.extraItems).includes(country) || country === Jurisdiction.US

export const getMinRegistrationPrice = (
  currency: Currency,
  countryList: Jurisdiction[],
  country?: Jurisdiction,
) =>
  country
    ? calculateOfferingPrice([country], currency).total
    : Math.min(...countryList.map((country) => calculateOfferingPrice([country], currency).total))

const dynamicPricesExpressionRegex = /\{\{(\w+)>([\w\-.]+)(?:>([\w]+)(!?))?\}\}/g

// Validates dynamic prices expressions: {{sheet>country>currency[!]}} or just {{sheet>country}}
// returns an array of validation errors (strings)
export const validateDynamicPricesExpressions = (text: string) =>
  Array.from(text.matchAll(/\{\{[^}]*\}\}/g))
    .map((match) => match[0])
    .map((expression) => {
      const expressionMatch = dynamicPricesExpressionRegex.exec(expression)
      dynamicPricesExpressionRegex.lastIndex = 0
      if (expressionMatch) {
        const sheetName = expressionMatch[1] as string
        if (!(sheetName in prices)) {
          return `Invalid prices sheet name '${sheetName}' in '${expression}'`
        }
        const sheet = prices[sheetName as keyof typeof prices]
        const country = expressionMatch[2] as string
        if (!(country in sheet)) {
          return `Invalid country '${country}' in '${expression}'`
        }
        if (expressionMatch.length > 3) {
          const currency = expressionMatch[3] as string
          if (currency && !(currency in sheet[country as keyof typeof sheet])) {
            return `Invalid currency '${currency}' in '${expression}'`
          }
        }
        return null
      } else {
        return `Invalid expression syntax: '${expression}'`
      }
    })
    .filter(Boolean)

// Substitutes `{{sheet>country[>currency[!]]}}` from `sheets` - no default, since the module price
// cache is empty on a prerendered page. No currency means EUR, and `current` only appends a
// conversion in brackets, so the prerendered figure doesn't change once preferences load.
export const renderDynamicPricesExpressions = (
  text: string,
  locale: Locale,
  sheets: DynamicPriceSheets,
  current?: Currency,
) =>
  text.replaceAll(
    dynamicPricesExpressionRegex,
    (
      match,
      sheet: string,
      country: string,
      currency: string | undefined,
      forceCurrencyMark: string | undefined,
    ) => {
      const format = (amount: number | undefined, as: Currency) =>
        amount === undefined ? null : formatMoney(amount, getFormattingOptions(as, locale, false))

      const base = (currency ?? currencies.EUR) as Currency
      const amount = format(_.get(sheets, [sheet, country, base]), base)

      // Keep the expression on a miss: `formatMoney` would quote the fee as zero.
      if (amount === null) {
        return match
      }

      const conversion =
        current && base !== current && !forceCurrencyMark
          ? format(_.get(sheets, [sheet, country, current]), current)
          : null

      return conversion === null ? amount : `${amount} (${conversion})`
    },
  )

// The entries a text's price expressions look up, so a page hands the browser only its own prices.
export const pickDynamicPriceSheets = (sheets: DynamicPriceSheets, text: string) => {
  const picked: DynamicPriceSheets = {}
  for (const [, sheet, country] of text.matchAll(dynamicPricesExpressionRegex)) {
    const entry = sheets[sheet as string]?.[country as string]
    if (entry) {
      picked[sheet as string] = {...picked[sheet as string], [country as string]: entry}
    }
  }
  return picked
}
